import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Col, Row, Spinner, Form, Button } from "react-bootstrap";
import API from "../../Utility";
import { ProjectContext } from "../../ProjectContext";
import Moment from "react-moment";
import ViewAssignedUsersTasksModal from '../WorkflowUsers/ViewAssignedUsersTasksModal';
import { FaCheck } from "react-icons/fa";
import Search from "../../Search";
import MultiSelectTypeahead from "../../MultiSelectTypeahead";
import Task from "./Task";
import ErrorModal from '../../ErrorModal/ErrorModal';
import { LuRefreshCcw } from 'react-icons/lu';

// Styles
import "./WorkflowTasks.scss";

export default function WorkflowTasks({ children, workflow, refreshUsers, setRefreshUsers, refreshTasks, setRefreshTasks, ...routeProps }) {
  let { workflowId } = useParams();
  const location = useLocation();
  let stateWorkflow = location && location.state ? location.state.workflow : [];
  const [workflowTasks, setWorkflowTasks] = useState(stateWorkflow);
  const [isLoading, setIsLoading] = useState(true);
  const { project } = useContext(ProjectContext);
  const [checkboxStatus, setCheckboxStatus] = useState({});
  const [assignmentToView, setAssignmentToView] = useState(null);
  const [showConfirmView, setShowConfirmView] = useState(false);

  const [nameFilter, setNameFilter] = useState(null);
  const [participantFilter, setParticipantFilter] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);

  const minSearchLength = 3;
  let pageSize = 50;
  let loadIncrement = 40;
  const [itemsToLoadPerPage, setItemsToLoadPerPage] = useState(loadIncrement);
  const [itemsLoaded, setItemsLoaded] = useState([]);
  const [page, setPage] = useState(1);
  const [lastTaskId, setLastTaskId] = useState(null);
  const [lastUserId, setLastUserId] = useState(null);
  const [loadMore, setLoadMore] = useState(true);
  const [moreItemsToLoad, setMoreItemsToLoad] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const getRoleName = (role) => {
    let roleIndex = workflow.assigneeRoles.findIndex((aRole) => aRole._id === role);
    if (roleIndex > -1) {
      return workflow.assigneeRoles[roleIndex].name;
    } else {
      return '';
    }
  };

  const getRoleUsers = (userWorkflow, role) => {
    let users = [];
    userWorkflow?.assignees?.map((assignee) => {
      if (assignee?.assigneeRole === role) {
        let assigneeUser = userWorkflow?.assigneeUsers?.find((u) => u._id === assignee?.user);
        if (assigneeUser?.displayName) {
          users.push(assigneeUser?.displayName);
          return assigneeUser?.displayName;
        } else {
          return '';
        }
      } else {
        return '';
      }
    });
    return users.join(', ');
  };

  async function getWorkflowTasksData(query) {
    try {
      setIsLoading(true);
      let apiWorkflow = await API.get({
        url: query.url,
      });
      setIsLoading(false);
      return apiWorkflow;
    } catch (error) {
      console.log(error);
    }
  }

  const completeTask = (user, task, userWorkflowTasksId, milestoneId, userWorkflowTasksIndex, milestoneIndex, taskIndex, toggleStatus) => {
    const onCompleteTaskSuccess = () => {
      let newWorkflowTasks = [ ...workflowTasks ];
      let milestoneIndexToModify = newWorkflowTasks?.[userWorkflowTasksIndex]?.workflow?.milestones?.findIndex((m) => m._id === milestoneId);
      if (milestoneIndexToModify > -1) {
        let taskIndexToRemove = newWorkflowTasks?.[userWorkflowTasksIndex]?.workflow?.milestones?.[milestoneIndex]?.tasks.findIndex((t) => t._id === task._id);
        newWorkflowTasks?.[userWorkflowTasksIndex]?.workflow?.milestones?.[milestoneIndexToModify]?.tasks.splice(taskIndexToRemove, 1);
        if (newWorkflowTasks?.[userWorkflowTasksIndex]?.workflow?.milestones?.[milestoneIndexToModify]?.tasks?.length === 0) {
          newWorkflowTasks[userWorkflowTasksIndex].workflow.milestones?.splice(milestoneIndexToModify, 1);
        }
        setWorkflowTasks(newWorkflowTasks);
      }
      setRefreshUsers(true);
      setRefreshTasks(false);
    };
    if (toggleStatus === true) {
      setTimeout(() => {
        onCompleteTaskSuccess();
      }, 100);
    }
    const callApi = async function() {
      try {
        const apiCallConfig = {
          url: `/admin/${project._id}/workflows/${workflow._id}/completeTask`,
        };
        apiCallConfig.data = {
          user: user._id,
          workflow: { _id: workflow._id },
          task: task._id
        };
        const res = await API.post(apiCallConfig);
        if (res.completed) {
          // onCompleteTaskSuccess();
        } else {
          throw new Error(res);
        }
      } catch (err) {
        console.error(err);
        setShowErrorModal(true);
      }
    };
    callApi();
  };

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const previousValues = usePrevious({ project, refreshTasks, nameFilter, participantFilter, assigneeFilter});

  // Load more items when the user scrolls through the loadIncrement number of items

  useEffect(() => {
    // console.log("Items loaded: ", itemsLoaded.length, " page: ", page);
    if (itemsLoaded.length >= itemsToLoadPerPage) {
      setItemsToLoadPerPage(itlpp => itlpp + (loadIncrement + (pageSize - loadIncrement)));
      // console.log("Load next page...");
      let newPage = page + 1;
      setPage(newPage);
      setLoadMore(true);
    }
  }, [itemsLoaded]);

  // Reset the API pagination and loaded data in the following scenarios:
  // - when the name filter changes
  // - when the participant filter changes
  // - when the assignee filter changes
  // - when the users refresh

  useEffect(() => {
    if (refreshTasks === true || (nameFilter !== previousValues?.nameFilter && (nameFilter?.length === 0 || nameFilter?.length >= minSearchLength)) || (participantFilter !== previousValues?.participantFilter && (participantFilter === "" || participantFilter?.length > 0)) || (assigneeFilter !== previousValues?.assigneeFilter && (assigneeFilter === "" || assigneeFilter?.length > 0))) {
      setWorkflowTasks([]);
      setPage(1);
      setLastTaskId(null);
      setLastUserId(null);
      setLoadMore(true);
      setMoreItemsToLoad(true);
      setItemsToLoadPerPage(loadIncrement);
      setItemsLoaded([]);
    }
  }, [refreshTasks, nameFilter, participantFilter, assigneeFilter]);

  useEffect(() => {
    async function initializeData() {
      // console.log("Loading more items... ", page);
      let url = `/admin/${project?._id}/workflows/${workflowId}/tasks`;
      let pagination = `?pageSize=${pageSize}`; // &page=${page}
      if (pagination) {
        url += pagination;
      }
      if (nameFilter) {
        url += pagination ? `&nameMatch=${nameFilter}` : `?nameMatch=${nameFilter}`;
      }
      if (participantFilter) {
        url += pagination || nameFilter ? `&participantsMatch=${participantFilter}` : `?participantsMatch=${participantFilter}`;
      }
      if (assigneeFilter) {
        url += pagination || nameFilter || participantFilter ? `&assigneesMatch=${assigneeFilter}` : `?assigneesMatch=${assigneeFilter}`;
      }
      if (lastTaskId) {
        url += pagination || nameFilter || participantFilter || assigneeFilter ? `&lastTaskId=${lastTaskId}` : `?lastTaskId=${lastTaskId}`;
      }
      if (lastUserId) {
        url += pagination || nameFilter || participantFilter || assigneeFilter || lastTaskId ? `&lastUserId=${lastUserId}` : `?lastUserId=${lastUserId}`;
      }
      let apiWorkflow = await getWorkflowTasksData({
        url: url,
      });
      let newWorkflowTasks = [ ...workflowTasks ];

      // If any of the filters have changed, or the users have been refreshed, reset the workflow tasks
      // Else add the new API tasks to the existing workflow tasks
      if (refreshTasks === true || (nameFilter !== previousValues?.nameFilter && (nameFilter?.length === 0 || nameFilter?.length >= minSearchLength)) || (participantFilter !== previousValues?.participantFilter && (participantFilter === "" || participantFilter?.length > 0)) || (assigneeFilter !== previousValues?.assigneeFilter && (assigneeFilter === "" || assigneeFilter?.length > 0))) {
        setWorkflowTasks(apiWorkflow.filteredResults);
      } else {
        for (let userWorkflow of apiWorkflow.filteredResults) {
          let userWorkflowTasksIndex = workflowTasks.findIndex((wt) => wt.user._id === userWorkflow.user._id);
          if (userWorkflowTasksIndex > -1) {
            // console.log('adding tasks to existing user workflow... ', userWorkflow.user.displayName);
            for (let userMilestone of userWorkflow.workflow.milestones) {
              let userMilestoneIndex = newWorkflowTasks[userWorkflowTasksIndex].workflow.milestones.findIndex((m) => m._id === userMilestone._id);
              if (userMilestoneIndex > -1) {
                for (let userTask of userMilestone.tasks) {
                  let userTaskIndex = newWorkflowTasks[userWorkflowTasksIndex].workflow.milestones[userMilestoneIndex].tasks.findIndex((t) => t._id === userTask._id);
                  if (userTaskIndex === -1) {
                    newWorkflowTasks[userWorkflowTasksIndex].workflow.milestones[userMilestoneIndex].tasks.splice(userTask.taskIndex, 0, userTask);
                  }
                }
              } else {
                newWorkflowTasks[userWorkflowTasksIndex].workflow.milestones.splice(userMilestone.milestoneIndex, 0, userMilestone);
              }
            }
          } else {
            newWorkflowTasks.push(userWorkflow);
          }
        }
        setWorkflowTasks(newWorkflowTasks);
      }

      setIsLoading(false);
      setRefreshUsers(false);
      setRefreshTasks(false);
      let lastLoadedUser = apiWorkflow?.filteredResults?.[apiWorkflow?.filteredResults.length - 1];
      let lastLoadedMilestone = lastLoadedUser?.workflow?.milestones?.[lastLoadedUser?.workflow?.milestones?.length - 1];
      let lastLoadedTask = lastLoadedMilestone?.tasks?.[lastLoadedMilestone?.tasks?.length - 1];
      setLastTaskId(lastLoadedTask?._id);
      setLastUserId(lastLoadedUser?.user?._id);
      setLoadMore(false);
      // The last API response indicates that there is no more data to load
      if (!apiWorkflow?.filteredResults?.[0]) {
        setMoreItemsToLoad(false);
      }
    }
    // If none of the filters have changed, and the users have not been refreshed, load more items
    if (project?._id && loadMore && moreItemsToLoad) {
      setIsLoading(true);
      initializeData();
    }
  }, [project, nameFilter, participantFilter, assigneeFilter, loadMore, moreItemsToLoad]);

  useEffect(() => {
    if (refreshTasks === true) {
      setCheckboxStatus({});
    }
  }, [refreshTasks]);

  return (<div>
    <div className="workflow-tasks__filters">
      <div className='filter-wrapper'>
        <Search
          style={{ height: '42px' }}
          placeholder="Name"
          labelKey="name"
          options={[]}
          isLoading={isLoading}
          isTypeAhead={false}
          minLength={minSearchLength}
          delay={450}
          callback={(text) => {
            return setNameFilter(text);
          }}
        />
      </div>
      <div className='filter-wrapper'>
        <MultiSelectTypeahead
          name='participants'
          displayName='Participants'
          url={`/admin/${project._id}/workflows/${workflow._id}/usersLookup?assigned=0&pageSize=1000`}
          placeholder="Participants"
          defaultValue={[]}

          autocomplete="off"
          minSearchLength={minSearchLength}
          initApiSearchOnChange={true}
          mapResponse={(response) => {
            return response.map(u => {
              return {
                name: u.displayName,
                ...u
              };
            });
          }}

          multiple={true}
          allowNewItems={false}
          preventSelectAll={true}
          dynamicHeight={true}

          className={`form-control`}
          onChange={(value) => {
            setParticipantFilter(value.map(v => v._id).join(','));
          }}
          onBlur={() => {}}
          resetInputAfterSelectOrRemove={true}
        />
      </div>
      {(project?.userPermissions?.isWorkflowManager || project?.userPermissions?.isCurrentUserOwner || project?.userPermissions?.isCurrentUserAdmin) && (
        <div className='filter-wrapper'>
          <MultiSelectTypeahead
            name='assignees'
            displayName='Assignees'
            url={`/admin/${project._id}/workflows/${workflow._id}/assignees`}
            placeholder="Assignees"
            defaultValue={[]}

            autocomplete="off"
            minSearchLength={minSearchLength}
            initApiSearchOnChange={true}
            mapResponse={(response) => {
              return response.map(u => {
                return {
                  name: u.displayName,
                  ...u
                };
              });
            }}

            multiple={true}
            allowNewItems={false}
            preventSelectAll={true}
            dynamicHeight={true}

            className={`form-control`}
            onChange={(value) => {
              setAssigneeFilter(value.map(v => v._id).join(','));
            }}
            onBlur={() => {}}
            resetInputAfterSelectOrRemove={true}
          />
        </div>
      )}
    </div>

    <div className='workflow-tasks-list__refresh'>
      <Button
        onClick={() => {
          setRefreshUsers(true);
          setRefreshTasks(true);
        }}
        disabled={isLoading}
      >
        <LuRefreshCcw className={isLoading ? "refresh-users spin" : "refresh-users"} style={{ verticalAlign: "middle", marginTop: "-2px", marginRight: "10px" }}></LuRefreshCcw>
        <span style={{ verticalAlign: "middle" }}> Refresh</span>
      </Button>
    </div>

    {(workflowTasks?.length > 0) ? (
      <div>
        <div className="workflow-tasks">
          {workflowTasks.map((userWorkflowTasks, userWorkflowTasksIndex) => {
            if (userWorkflowTasks?.workflow?.milestones?.length > 0) {
              return (
                <div key={userWorkflowTasksIndex}>
                  <Row style={{ width: '100%', margin: '0 auto' }}>
                    <Col>
                      <Row style={{ backgroundColor: '#eee', padding: '20px 10px', borderRadius: '10px' }}>
                        <Col style={{ display: 'flex', alignItems: 'center' }}>
                          <b>{userWorkflowTasks.user.displayName}</b>
                        </Col>
                        <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            onClick={() => {
                              setAssignmentToView(userWorkflowTasks);
                              setShowConfirmView(true);
                            }}
                            variant="outline-secondary"
                            style={{ display: 'flex', alignItems: 'center', marginRight: '10px'  }}
                          >
                            <FaCheck style={{ verticalAlign: "middle", marginRight: '10px' }} />
                            <span style={{ verticalAlign: "middle" }}>All Tasks</span>
                          </Button>
                        </Col>
                        {/* <Col style={{ textAlign: 'right' }}>
                        <b>Due Date: <Moment format="DD MMM 'YY" add={{ days: userWorkflowTasks?.workflow?.milestones?.[userWorkflowTasks?.workflow?.milestones?.length - 1]?.endDay }}>{userWorkflowTasks?.startDate}</Moment></b>
                      </Col> */}
                      </Row>
                      <br></br>
                      <Row style={{ margin: '0 auto' }}>
                        <Col>
                          <Row>
                            <Col>
                              {userWorkflowTasks?.workflow?.milestones?.sort((a, b) => a.milestoneIndex - b.milestoneIndex)?.map((milestone, milestoneIndex) => {
                                if (milestone?.tasks?.length > 0) {
                                  return (
                                    <div key={`milestone-${userWorkflowTasksIndex}-${milestoneIndex}`}>
                                      <Row>
                                        <b>{milestone.name} - </b>&nbsp; Start Date: &nbsp;<Moment format="DD MMM 'YY" add={{ days: milestone.startDate - 1 }}>{userWorkflowTasks.startDate}</Moment>
                                        <br></br>
                                        <br></br>
                                      </Row>
                                      <Row key={`milestone-tasks-${userWorkflowTasksIndex}-${milestoneIndex}`} style={{ padding: '0 0 20px 0' }}>
                                        <Col>
                                          {milestone?.tasks?.sort((a, b) => a.taskIndex - b.taskIndex)?.map((task, taskIndex) => {
                                            return (
                                              <Task
                                                key={`${userWorkflowTasksIndex}-${milestoneIndex}-${taskIndex}`}
                                                userWorkflowTasksIndex={userWorkflowTasksIndex}
                                                milestoneIndex={milestoneIndex}
                                                taskIndex={taskIndex}
                                                milestone={milestone}
                                                checkboxStatus={checkboxStatus}
                                                userWorkflowTasks={userWorkflowTasks}
                                                task={task}
                                                completeTask={completeTask}
                                                setCheckboxStatus={setCheckboxStatus}
                                                getRoleName={getRoleName}
                                                getRoleUsers={getRoleUsers}
                                                itemsToLoadPerPage={itemsToLoadPerPage}
                                                setItemsToLoadPerPage={setItemsToLoadPerPage}
                                                itemsLoaded={itemsLoaded}
                                                setItemsLoaded={setItemsLoaded}
                                                user={userWorkflowTasks.user}
                                              />
                                            );
                                          })}
                                        </Col>
                                      </Row>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div key={`milestone-${userWorkflowTasksIndex}-${milestoneIndex}`}></div>
                                  );
                                }
                              })}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <br></br>
                </div>
              );
            } else {
              return <span key={userWorkflowTasksIndex}></span>;
            }
          })}
        </div>
        <ViewAssignedUsersTasksModal
          workflow={workflow}
          user={assignmentToView}
          show={showConfirmView}
          onCancel={() => {
            setShowConfirmView(false);
            setAssignmentToView(null);
            setRefreshUsers(true);
            setRefreshTasks(true);
          }}
          onSuccess={() => {
            setShowConfirmView(false);
            setAssignmentToView(null);
            setRefreshUsers(true);
            setRefreshTasks(true);
          }}
        />
      </div>
    ) : (!isLoading) ? (
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
        No results
      </div>
    ) : (
      <></>
    )}
    {(isLoading) && (
      <div style={{ display: 'flex', width: '100%', justifyContent: 'center', marginBottom: '50px' }}>
        <Spinner animation="border" />
      </div>
    )}
    <ErrorModal
      show={showErrorModal}
      onHide={() => setShowErrorModal(false)}
      title="Error"
      body="There was an error completing the task. Please reload and try again."
      confirmtext="OK"
    />
  </div>);
}